import moment from "moment";
import { isValueNullish } from "../utils";

window.vesl = window.vesl || {};

function ApiService(
	$http,
	$q,
	config
) {
	const service = this;
	const baseUrl = config.apiUrl;

	const resolve = (returnFullResponse) => (response) => {
		if(!response) {
			return reject(returnFullResponse)(response);
		}

		if(returnFullResponse) {
			return response;
		}

		if(response.data?.Success) {
			return response.data.Data;
		}

		else if(response.config.url === baseUrl) {
			return response.data;
		}

		return reject(returnFullResponse)(response);
	};

	function reject(returnFullResponse) {
		return (response) => {
			const returnVal = returnFullResponse || isValueNullish(response?.data)
				? response
				: response.data;

			return $q.reject(returnVal);
		};
	}

	service.delete = apiDelete;
	service.get = apiGet;
	service.post = apiPost;
	service.put = apiPut;
	service.downloadCsv = downloadCsv;
	service.setDefaultHeader = setDefaultHeader;
	service.http = customApi;

	setDefaultHeader("Cache-Control", "no-cache");
	setDefaultHeader("Pragma", "no-cache");
	setDefaultHeader("Expires", "-1");

	function apiGet(endpoint, httpConfig = {}, returnFullResponse = false) {
		return $http.get(baseUrl + endpoint, httpConfig).then(resolve(returnFullResponse), reject(returnFullResponse));
	}

	function downloadCsv(endpoint) {
		return $http({
			method: "GET",
			url: baseUrl + endpoint,
			headers: { Accept: "text/csv" }
		})
			.success(data => {
				const anchor = angular.element("<a/>");
				anchor
					.attr({
						href: "data:attachment/csv;charset=utf-8," + encodeURI(data),
						target: "_blank",
						download: `VESL_Reports_${moment().format("MM-DD-YYYY-HH-mm-ss")}.csv`
					})[0]
					.click();
			});
	}

	function apiPost(endpoint, data, httpConfig = {}, returnFullResponse = false) {
		return $http
			.post(baseUrl + endpoint, data, httpConfig)
			.then((response) => {
				if(!Object.prototype.hasOwnProperty.call(httpConfig, "headers") || !Object.prototype.hasOwnProperty.call(httpConfig.headers, "Accept")) {
					return resolve(returnFullResponse)(response);
				}

				return response.data;
			}, reject(returnFullResponse));
	}

	function apiPut(endpoint, data, httpConfig = {}, returnFullResponse = false) {
		return $http.put(baseUrl + endpoint, data, httpConfig).then(resolve(returnFullResponse), reject(returnFullResponse));
	}

	function apiDelete(endpoint, data) {
		return $http({
			url: baseUrl + endpoint,
			method: "DELETE",
			data: data,
			headers: {
				"Content-Type": "application/json"
			}
		});
	}

	function customApi(config) {
		return $http(config);
	}

	function setDefaultHeader(key, value) {
		$http.defaults.headers.common[key] = value;
	}

	return service;
}

const apiServiceModule = angular.module("vesl.services.api", []);

apiServiceModule.factory("http:request-authorization-interceptor", function($q, openIdService, $injector, toastr, config) {
	const retryFlag = "__veslAuthRetry";

	const isExternalRequest = (httpConfig) => {
		return !httpConfig.url.startsWith(config.apiUrl);
	};

	const rejectAuthFailure = (rejection, error) => {
		toastr.warning("We couldn't refresh your session. Please retry the request in a moment.", "", {
			timeout: 5000
		});

		return $q.reject(error || rejection);
	};

	return {
		request: function(httpConfig) {
			if(isExternalRequest(httpConfig)) {
				return httpConfig;
			}

			const promise = openIdService.getAccessToken()
				.then(token => {
					httpConfig.headers = httpConfig.headers || {};
					if(token) {
						httpConfig.headers["Authorization"] = `Bearer ${token}`;
					}
					return httpConfig;
				});
			return promise;
		},
		requestError: function(rejection) {
			return $q.reject(rejection);
		},
		response: function(response) {
			return response;
		},
		responseError: function(rejection) {
			if(rejection.status === 401 && rejection.config && !rejection.config[retryFlag]) {
				rejection.config[retryFlag] = true;

				return openIdService.renewAccessToken()
					.then(user => {
						if(!user?.access_token) {
							return $q.reject(rejection);
						}

						const $http = $injector.get("$http");
						rejection.config.headers = rejection.config.headers || {};
						rejection.config.headers["Authorization"] = `Bearer ${user.access_token}`;

						return $http(rejection.config);
					})
					.catch(error => rejectAuthFailure(rejection, error));
			}

			return $q.reject(rejection);
		}
	};
});

apiServiceModule.config(["$httpProvider", http => http.interceptors.push("http:request-authorization-interceptor")]);

apiServiceModule.factory("http:retry-after-interceptor", function($q, $injector) {
	const parseRetryAfter = (retryAfter) => {
		const seconds = parseInt(retryAfter, 10);
		return isNaN(seconds) ? 0 : seconds * 1000;
	};

	return {
		responseError: function(rejection) {
			if(rejection.status === 429) {
				const retryAfter = rejection.headers && rejection.headers("Retry-After");
				const delay = parseRetryAfter(retryAfter);
				const deferred = $q.defer();
				const $timeout = $injector.get("$timeout");
				const $http = $injector.get("$http");
				$timeout(() => $http(rejection.config).then(deferred.resolve, deferred.reject), delay);
				return deferred.promise;
			}
			return $q.reject(rejection);
		}
	};
});

apiServiceModule.config(["$httpProvider", http => http.interceptors.push("http:retry-after-interceptor")]);

export default apiServiceModule.service("api", ApiService).name;
